import React from 'react';

import { get } from 'lodash';
import { MdOutlineAssignmentLate } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import {
  useCreatePurchaseOrderMutation,
  useUpdateItemPurchaseOrderMutation,
} from '@modules/rtk/esb-api';
import {
  Button,
  Card,
  organizationGuidSel,
  userDataSel,
} from '@modules/shared';
import { toastService } from '@modules/toasts';

import { POStatusTypes } from '../../constants';
import { selectedAdvancementsPendingItem } from '../../duck/types';
import { createPayloadAddItemPO } from '../../utils';
import styles from './POCardPlaceholder.less';

type SelectedItemKey = string | number;

interface POCardPlaceholderProps {
  status: string;
  selectedAdvancements: selectedAdvancementsPendingItem[];
  createDisabled: boolean;
  isLoading: boolean;
  onSetSelectedRowKeys: (items: SelectedItemKey[]) => void;
}

export const POCardPlaceholder: React.FC<POCardPlaceholderProps> = ({
  status,
  selectedAdvancements = [],
  createDisabled = false,
  isLoading = false,
  onSetSelectedRowKeys,
}) => {
  const dispatch = useDispatch();
  const selectedOrgGuid = useSelector(organizationGuidSel);
  const userData = useSelector(userDataSel);
  const { userId = null } = userData?.profile || {};
  const [createPO, { isLoading: createPOisLoading }] =
    useCreatePurchaseOrderMutation();
  const [addItems, { isLoading: addItemsisLoading }] =
    useUpdateItemPurchaseOrderMutation();

  const handleClick = async () => {
    try {
      const payload = {
        organizationGuid: selectedOrgGuid,
        body: {
          userId: userId,
        },
      };

      const result = await createPO(payload).unwrap();

      const payloadAddItem = createPayloadAddItemPO(
        selectedOrgGuid,
        result.id,
        selectedAdvancements,
      );

      await addItems(payloadAddItem).unwrap();

      toastService.success(
        <FormattedMessage id="PurchaseOrder.PODetailsPage.success" />,
      );
    } catch (error) {
      const errorMessage = get(error, ['data', 'errorDesc']);
      if (errorMessage) {
        toastService.error(errorMessage);
      }
    }
    dispatch(onSetSelectedRowKeys([]));
  };

  return (
    <Card
      margin
      rounded
      bordered
      loading={isLoading || createPOisLoading || addItemsisLoading}
    >
      <div className={styles.placeholderContainer}>
        <MdOutlineAssignmentLate className={styles.assignmentIcon} />
        <span className={styles.placeholderText}>
          <FormattedMessage
            id="PurchaseOrder.POCardPlaceholder.text"
            values={{ status: status }}
          />
        </span>
        {status === POStatusTypes.OPEN && (
          <FormattedMessage id="PurchaseOrder.POCardPlaceholder.subtext" />
        )}
        {status === POStatusTypes.OPEN && (
          <Button
            onClick={handleClick}
            type="primary"
            shape="round"
            size="large"
            uppercase={false}
            className={styles.actionButton}
            disabled={createDisabled}
          >
            <FormattedMessage id="PurchaseOrder.ToPurchaseTab.createButton" />
          </Button>
        )}
      </div>
    </Card>
  );
};
